import Modal from '@components/v2/ui/Modal/Modal';
import Typography from '@components/v2/ui/Typography/Typography';
import { Box, CircularProgress, Stack } from '@mui/material';
import { Virtuoso } from 'react-virtuoso';
import LangEnum from '../../../../lang/LangEnum';
import { useInfiniteOrderInvoicesList } from '../../../../services/modules/orderInvoices/orderInvoices.queries';
import { formatDate } from '../../../../utils/dates';
import { OrderConditionsTestIds } from '../../../../utils/testIds/OrderConditions.testIds';
import { useTranslation } from '../../../translation/TranslationContext';

interface OrderInvoicesModalProps {
  orderId: string;
  isOpen: boolean;
  onClose: () => void;
}

const MODAL_HEIGHT = 170;

const OrderInvoicesModal = ({ orderId, isOpen, onClose }: OrderInvoicesModalProps) => {
  const { translate } = useTranslation();

  const { query } = useInfiniteOrderInvoicesList({ orderId }, { isEnabled: isOpen });

  return (
    <Modal
      isOpen={isOpen}
      size="standard"
      header={{ title: LangEnum.ORDER_INVOICES_MODAL_TITLE }}
      testId={OrderConditionsTestIds.ORDER_INVOICES_MODAL}
      onClose={onClose}
    >
      {query.isLoading ? (
        <Stack sx={{ height: MODAL_HEIGHT, alignItems: 'center', justifyContent: 'center' }}>
          <CircularProgress />
        </Stack>
      ) : (
        <Virtuoso
          style={{ height: MODAL_HEIGHT }}
          data={query.pagesData}
          endReached={query.fetchNextPage}
          itemContent={(_, invoice) => (
            <Box
              key={invoice.id}
              sx={{
                display: 'flex',
                flexDirection: 'row',
                justifyContent: 'space-between',
                paddingY: 0.75,
                paddingX: 0.75,
              }}
            >
              <Typography>
                {translate(LangEnum.ORDER_INVOICES_MODAL_INVOICE_CODE, { code: invoice.code })}
              </Typography>
              {invoice.issuedAt && (
                <Typography variant="body2" color="textSecondary">
                  {translate(LangEnum.ORDER_INVOICES_MODAL_INVOICE_ISSUED_DATE, {
                    date: formatDate(invoice.issuedAt),
                  })}
                </Typography>
              )}
            </Box>
          )}
        />
      )}
    </Modal>
  );
};

export default OrderInvoicesModal;
